/**
 * CORE-1730 — черновик мобильного веб-композера: DOM поля-редактора ↔ текст.
 *
 * Поле держит токен `:agape_N:` картинкой `<img data-token>`, а наружу отдаёт
 * тот же текст, что отдавало текстовое поле: токены, переносы строк,
 * упоминания как есть. Здесь только разбор и сборка, без React и без
 * `document` — узлы и фабрика приходят снаружи, поэтому проверяется в jest
 * на простых объектах.
 */
import { parseEmojiTokens } from '../../utils/emojiTokens';

export const DRAFT_TOKEN_ATTR = 'data-token';
const ZERO_WIDTH_SPACE = '\u200B';
const BLOCKS = new Set(['DIV', 'P']);

/** Минимум от DOM-узла, чтобы прочитать черновик. */
export interface DraftNodeLike {
  nodeType: number;
  nodeName: string;
  nodeValue?: string | null;
  childNodes: ArrayLike<DraftNodeLike>;
  getAttribute?: (name: string) => string | null;
}

/**
 * DOM поля → текст с токенами. Блок (`<div>`, которым браузер оборачивает
 * новую строку) — перенос; `<br>` — перенос, кроме последнего в строке: его
 * браузер держит ради курсора. Неразрывные пробелы и якорь курсора убираются.
 */
export function draftTextFromNodes(root: DraftNodeLike): string {
  let out = '';
  const walk = (node: DraftNodeLike) => {
    const kids = node.childNodes;
    for (let i = 0; i < kids.length; i += 1) {
      const child = kids[i];
      if (child.nodeType === 3) {
        out += (child.nodeValue ?? '').split(ZERO_WIDTH_SPACE).join('').replace(/\u00A0/g, ' ');
        continue;
      }
      if (child.nodeType !== 1) continue;
      const name = child.nodeName.toUpperCase();
      if (name === 'IMG') {
        const token = child.getAttribute?.(DRAFT_TOKEN_ATTR);
        if (token) out += `:${token}:`;
        continue;
      }
      if (name === 'BR') {
        if (i < kids.length - 1) out += '\n';
        continue;
      }
      if (BLOCKS.has(name) && out.length > 0) out += '\n';
      walk(child);
    }
  };
  walk(root);
  return out;
}

/** Место в DOM поля: узел и смещение в нём (как у `Range`). */
export interface DraftDomPoint<N extends DraftNodeLike = DraftNodeLike> {
  container: N;
  offset: number;
}

const cleanText = (value: string | null | undefined) =>
  (value ?? '').split(ZERO_WIDTH_SPACE).join('').replace(/\u00A0/g, ' ');

/**
 * CORE-1791 (возврат QA 23.10) — где в тексте черновика стоит курсор и что
 * прямо перед ним: картинка комплекта или обычный знак.
 *
 * Тот же обход, что у `draftTextFromNodes`, только с остановкой в точке
 * курсора: смещение считается в тексте с токенами (картинка — `:agape_N:`,
 * якорь курсора — ноль). По нему вставка из палитры встаёт туда, где курсор,
 * а Backspace за картинкой убирает картинку, а не невидимый якорь.
 */
export function draftCaretInfo(
  root: DraftNodeLike,
  point: DraftDomPoint,
): { text: string; offset: number; tokenBefore: string | null } {
  let out = '';
  let at: number | null = null;
  let tokenBefore: string | null = null;
  // Последняя единица текста перед курсором: картинка (её токен) или знак.
  let lastToken: string | null = null;
  const mark = () => {
    if (at === null) { at = out.length; tokenBefore = lastToken; }
  };
  const walk = (node: DraftNodeLike) => {
    const kids = node.childNodes;
    for (let i = 0; i < kids.length; i += 1) {
      if (node === point.container && i === point.offset) mark();
      const child = kids[i];
      if (child.nodeType === 3) {
        const raw = child.nodeValue ?? '';
        if (child === point.container) {
          const head = cleanText(raw.slice(0, point.offset));
          out += head;
          if (head) lastToken = null;
          mark();
          const tail = cleanText(raw.slice(point.offset));
          out += tail;
          if (tail) lastToken = null;
        } else {
          const t = cleanText(raw);
          out += t;
          if (t) lastToken = null;
        }
        continue;
      }
      if (child.nodeType !== 1) continue;
      const name = child.nodeName.toUpperCase();
      if (name === 'IMG') {
        const token = child.getAttribute?.(DRAFT_TOKEN_ATTR);
        if (token) { out += `:${token}:`; lastToken = token; }
        continue;
      }
      if (name === 'BR') {
        if (i < kids.length - 1) { out += '\n'; lastToken = null; }
        continue;
      }
      if (BLOCKS.has(name) && out.length > 0) { out += '\n'; lastToken = null; }
      walk(child);
    }
    if (node === point.container && point.offset >= kids.length) mark();
  };
  walk(root);
  return { text: out, offset: at ?? out.length, tokenBefore };
}

/**
 * Обратное к `draftCaretInfo` для поля, собранного `draftNodesFromText`
 * (плоский ряд: текст, картинки, переносы и якорь): точка DOM для смещения в
 * тексте с токенами. Курсор после картинки — позиция за ней в корне.
 */
export function draftPointAt<N extends DraftNodeLike>(root: N, offset: number): DraftDomPoint<N> {
  const kids = root.childNodes as ArrayLike<N>;
  let out = 0;
  for (let i = 0; i < kids.length; i += 1) {
    const child = kids[i];
    if (child.nodeType === 3) {
      const raw = child.nodeValue ?? '';
      const len = cleanText(raw).length;
      if (len > 0 && offset <= out + len) return { container: child, offset: rawIndex(raw, offset - out) };
      out += len;
      continue;
    }
    if (child.nodeType !== 1) continue;
    const name = child.nodeName.toUpperCase();
    const token = name === 'IMG' ? child.getAttribute?.(DRAFT_TOKEN_ATTR) : null;
    const len = token ? token.length + 2 : name === 'BR' && i < kids.length - 1 ? 1 : 0;
    if (len === 0) continue;
    if (offset <= out) return { container: root, offset: i };
    out += len;
    if (offset <= out) return { container: root, offset: i + 1 };
  }
  return { container: root, offset: kids.length };
}

/** Смещение в сыром тексте узла по смещению в черновике: якорь курсора длины не имеет. */
function rawIndex(raw: string, want: number): number {
  if (want <= 0) return 0;
  let seen = 0;
  for (let k = 0; k < raw.length; k += 1) {
    if (raw[k] === ZERO_WIDTH_SPACE) continue;
    seen += 1;
    if (seen === want) return k + 1;
  }
  return raw.length;
}

/** Вставить токен в текст черновика по смещению курсора; курсор — сразу за ним. */
export function insertDraftToken(text: string, offset: number, token: string): { text: string; caret: number } {
  const at = Math.max(0, Math.min(offset, text.length));
  const piece = `:${token}:`;
  return { text: text.slice(0, at) + piece + text.slice(at), caret: at + piece.length };
}

/** Убрать токен, который стоит прямо перед курсором (Backspace за картинкой). */
export function removeDraftTokenBefore(text: string, offset: number, token: string): { text: string; caret: number } | null {
  const piece = `:${token}:`;
  if (offset < piece.length || text.slice(offset - piece.length, offset) !== piece) return null;
  return { text: text.slice(0, offset - piece.length) + text.slice(offset), caret: offset - piece.length };
}

/** Как собрать узлы поля; `image` возвращает null для незнакомого токена — он остаётся текстом. */
export interface DraftNodeFactory<N> {
  text: (value: string) => N;
  image: (token: string) => N | null;
  lineBreak: () => N;
}

/** Текст с токенами → узлы поля: текст, картинки комплекта, переносы. */
export function draftNodesFromText<N>(text: string, make: DraftNodeFactory<N>): N[] {
  const out: N[] = [];
  for (const seg of parseEmojiTokens(text)) {
    if (seg.type === 'emoji') {
      const image = make.image(seg.reaction);
      out.push(image ?? make.text(`:${seg.reaction}:`));
      continue;
    }
    seg.value.split('\n').forEach((line, i) => {
      if (i > 0) out.push(make.lineBreak());
      if (line) out.push(make.text(line));
    });
  }
  return out;
}
